import axios from "axios";
import { API_PATH } from "./apiPath";
import { getCookie } from "./cookie";
import { useAuthStore } from "@/store/useAuthStore";

const api = axios.create({
  baseURL: process.env.NEXT_PUBLIC_API_URL,
});

// Add a request interceptor to include the token
api.interceptors.request.use((config) => {
  const token = getCookie("access_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const signIn = async (params) => {
  try {
    const res = await api.post(API_PATH.AUTH.SIGN_IN, params);
    return res.data;
  } catch (error) {
    throw error;
  }
};

export const signUp = async (params) => {
  try {
    const res = await api.post(API_PATH.AUTH.SIGN_UP, params);
    return res.data;
  } catch (error) {
    throw error;
  }
};

export const forgotPassword = async (params) => {
  try {
    const res = await api.post(API_PATH.AUTH.FORGOT_PASSWORD, params);
    return res.data;
  } catch (error) {
    throw error;
  }
};

export const resendOtp = async (params) => {
  try {
    const res = await api.post(API_PATH.AUTH.RESEND_OTP, params);
    return res.data;
  } catch (error) {
    throw error;
  }
};

export const resendSignupOtp = async (params) => {
  try {
    const res = await api.post(API_PATH.AUTH.RESEND_SIGNUP_OTP, params);
    return res.data;
  } catch (error) {
    throw error;
  }
};

export const resendForgotPasswordOtp = async (params) => {
  try {
    const res = await api.post(
      API_PATH.AUTH.RESEND_FORGOT_PASSWORD_OTP,
      params,
    );
    return res.data;
  } catch (error) {
    throw error;
  }
};

export const verifyOtp = async (params) => {
  try {
    const res = await api.post(API_PATH.AUTH.VERIFY_OTP, params);
    return res.data;
  } catch (error) {
    throw error;
  }
};

export const verifyForgotPasswordOtp = async (params) => {
  try {
    const res = await api.post(API_PATH.AUTH.FORGOT_OTP_VERIFY, params);
    return res.data;
  } catch (error) {
    throw error;
  }
};

export const verifySignupOtp = async (params) => {
  try {
    const res = await api.post(API_PATH.AUTH.SIGN_UP_OTP_VERIFY, params);
    return res.data;
  } catch (error) {
    throw error;
  }
};

export const resetPassword = async (params) => {
  try {
    const res = await api.post(API_PATH.AUTH.RESET_PASSWORD, params);
    return res.data;
  } catch (error) {
    throw error;
  }
};

export const changePassword = async (params) => {
  try {
    const res = await api.post(API_PATH.AUTH.CHANGE_PASSWORD, params);
    return res.data;
  } catch (error) {
    throw error;
  }
};

export const logOut = async () => {
  try {
    const res = await api.get(API_PATH.AUTH.LOG_OUT);
    return res.data;
  } catch (error) {
    throw error;
  }
};

// Accept terms and privacy policy
export const acceptTerms = async () => {
  try {
    console.log("acceptTerms called");
    const res = await api.post(API_PATH.AUTH.ACCEPT_TERMS);
    return res.data;
  } catch (error) {
    throw error;
  }
};

// Social Sign-In (OAuth)
export const socialSignIn = async (params) => {
  try {
    const res = await api.post(API_PATH.AUTH.SOCIAL_SIGNIN, params);
    return res.data;
  } catch (error) {
    throw error;
  }
};

export const updatePreferences = async (params) => {
  try {
    const res = await api.put(API_PATH.PROFILE.UPDATE_PREFERENCES, params);
    return res.data;
  } catch (error) {
    throw error;
  }
};

export const updateProfile = async (params) => {
  try {
    const res = await api.put(API_PATH.PROFILE.UPDATE_PROFILE, params);
    return res.data;
  } catch (error) {
    throw error;
  }
};

export const upgradeProfileRole = async () => {
  try {
    const res = await api.post(API_PATH.PROFILE.UPGRADE);
    return res.data;
  } catch (error) {
    throw error;
  }
};

export const getProfileData = async () => {
  try {
    const res = await api.get(API_PATH.PROFILE.PROFILE_DATA);
    // Update the auth store with the latest profile data
    const { setUser } = useAuthStore.getState();
    setUser(res?.data?.data);
    return res.data;
  } catch (error) {
    throw error;
  }
};

export const uploadFile = async (body) => {
  try {
    const res = await api.post(API_PATH.UPLOAD, body, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return res?.data;
  } catch (err) {
    throw err;
  }
};

export const uploadSelfie = async (file) => {
  try {
    const formData = new FormData();
    formData.append("file", file);
    const res = await api.post(API_PATH.UPLOAD_SELFIE, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return res?.data;
  } catch (err) {
    throw err;
  }
};

export const getProfileStatus = async () => {
  try {
    const res = await api.get(API_PATH.PROFILE.PROFILE_STATUS);
    return res.data;
  } catch (error) {
    throw error;
  }
};

export const getQuestions = async () => {
  try {
    const res = await api.get(API_PATH.PROFILE.QUESTIONS);
    return res.data;
  } catch (error) {
    throw error;
  }
};

export const userPhotos = async (body) => {
  try {
    const res = await api.post(API_PATH.PROFILE.USER_PHOTOS, body);
    return res.data;
  } catch (error) {
    throw error;
  }
};

export const allPlans = async () => {
  try {
    const res = await api.get(API_PATH.WALLET.PLANS);
    return res.data;
  } catch (error) {
    throw error;
  }
};

export const getNotificationSettings = async () => {
  try {
    const res = await api.get(API_PATH.NOTIFICATIONS.SETTINGS);
    return res.data;
  } catch (error) {
    throw error;
  }
};

export const updateNotificationSettings = async (params) => {
  try {
    const res = await api.put(API_PATH.NOTIFICATIONS.SETTINGS, params);
    return res.data;
  } catch (error) {
    throw error;
  }
};

export const getFaqs = async (params = {}) => {
  try {
    const res = await api.get(API_PATH.FAQS.LIST, { params });
    return res.data;
  } catch (error) {
    throw error;
  }
};

export const createPaymentIntent = async (params) => {
  try {
    const res = await api.post(API_PATH.WALLET.CREATE_PAYMENT_INTENT, params);
    return res.data;
  } catch (error) {
    throw error;
  }
};

export const joinCircle = async (params) => {
  try {
    const res = await api.post(API_PATH.CIRCLE.JOIN, params);
    return res.data;
  } catch (error) {
    throw error;
  }
};

export const getMyCircleMemberships = async () => {
  try {
    const res = await api.get(API_PATH.CIRCLE.MY_MEMBERSHIPS);
    return res?.data?.data;
  } catch (error) {
    throw error;
  }
};

export const getCircleDetails = async (ownerUserId) => {
  try {
    const res = await api.get(`${API_PATH.CIRCLE.DETAILS}/${ownerUserId}`);
    return res?.data?.data;
  } catch (error) {
    throw error;
  }
};

export const getCircleMembers = async () => {
  try {
    const res = await api.get(API_PATH.CIRCLE.MEMBERS);
    return res?.data?.data;
  } catch (error) {
    throw error;
  }
};

export const deactivateCircleMember = async (memberUserId) => {
  try {
    const res = await api.patch(
      `${API_PATH.CIRCLE.MEMBERS}/${memberUserId}/deactivate`,
    );
    return res?.data;
  } catch (error) {
    throw error;
  }
};

export const activateCircleMember = async (memberUserId) => {
  try {
    const res = await api.patch(
      `${API_PATH.CIRCLE.MEMBERS}/${memberUserId}/activate`,
    );
    return res?.data;
  } catch (error) {
    throw error;
  }
};

export const removeCircleMember = async (memberUserId) => {
  try {
    const res = await api.delete(`${API_PATH.CIRCLE.MEMBERS}/${memberUserId}`);
    return res?.data;
  } catch (error) {
    throw error;
  }
};

export const swapCircleMembers = async ({
  activeMemberUserId,
  backupMemberUserId,
}) => {
  try {
    const res = await api.patch(API_PATH.CIRCLE.MEMBERS_SWAP, {
      activeMemberUserId,
      backupMemberUserId,
    });
    return res?.data;
  } catch (error) {
    throw error;
  }
};

export const getCircleVoteRequests = async (params = {}) => {
  try {
    const { page = 1, limit = 20 } = params;
    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    const res = await api.get(
      `${API_PATH.CIRCLE.VOTE_REQUESTS}?${queryParams.toString()}`,
    );
    return res?.data?.data;
  } catch (error) {
    throw error;
  }
};

export const getCircleVoteRequestByMatchId = async (matchId) => {
  try {
    const res = await api.get(`${API_PATH.CIRCLE.VOTE_REQUESTS}/${matchId}`);
    return res?.data?.data;
  } catch (error) {
    throw error;
  }
};

export const getCircleMatchStatus = async (matchId) => {
  try {
    const res = await api.get(`${API_PATH.CIRCLE.MATCH_STATUS}/${matchId}`);
    return res?.data?.data;
  } catch (error) {
    throw error;
  }
};

export const getTransactions = async (params = {}) => {
  try {
    const {
      page = 1,
      limit = 10,
      orderBy = "createdAt",
      order = "desc",
      type,
    } = params;

    // Build query params
    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      orderBy,
      order: order.toLowerCase(),
    });

    // Add type filter if provided
    if (type && ["CREDIT", "DEBIT"].includes(type)) {
      queryParams.append("type", type);
    }

    const res = await api.get(
      `${API_PATH.WALLET.TRANSACTIONS}?${queryParams.toString()}`,
    );
    return res.data;
  } catch (error) {
    throw error;
  }
};

export const getNotifications = async (params = {}) => {
  try {
    const { page = 1, limit = 20 } = params;
    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    const res = await api.get(
      `${API_PATH.NOTIFICATIONS.LIST}?${queryParams.toString()}`,
    );
    return res.data;
  } catch (error) {
    throw error;
  }
};

// Delete Account
export const deleteAccount = async () => {
  try {
    const res = await api.delete(API_PATH.PROFILE.DELETE_ACCOUNT);
    return res.data;
  } catch (error) {
    throw error;
  }
};

export const pauseAccount = async () => {
  try {
    const res = await api.patch(API_PATH.PROFILE.PAUSE_ACCOUNT);
    return res.data;
  } catch (error) {
    throw error;
  }
};

export const deletePhoto = async (photoId) => {
  try {
    const res = await api.delete(`/user/me/photos/${photoId}?permanent=true`);
    return res.data;
  } catch (error) {
    throw error;
  }
};

export const getDashboardUsers = async (params = {}) => {
  try {
    const res = await api.get(API_PATH.DASHBOARD.DASHBOARD_USERS, {
      params,
    });
    return res?.data?.data;
  } catch (error) {
    throw error;
  }
};

export const getOneUser = async (userId) => {
  try {
    const res = await api.get(`${API_PATH.DASHBOARD.ONE_USER}/${userId}`);
    return res?.data?.data;
  } catch (error) {
    throw error;
  }
};

export const unlockProfile = async (userId) => {
  try {
    const res = await api.get(
      `${API_PATH.DASHBOARD.UNLOCKED_PROFILE}/${userId}`,
    );
    // Return full response so callers can access res.data.message
    return res?.data;
  } catch (error) {
    throw error;
  }
};


export const getLikedMe = async (params = {}) => {
  try {
    const { page = 1, limit = 20 } = params;
    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    const res = await api.get(
      `${API_PATH.MATCHES.LIKED_ME}?${queryParams.toString()}`,
    );
    return res?.data?.data;
  } catch (error) {
    throw error;
  }
};

export const getILiked = async (params = {}) => {
  try {
    const { page = 1, limit = 20 } = params;
    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    const res = await api.get(
      `${API_PATH.MATCHES.I_LIKED}?${queryParams.toString()}`,
    );
    return res?.data?.data;
  } catch (error) {
    throw error;
  }
};

export const getVisitors = async (params = {}) => {
  try {
    const { page = 1, limit = 20 } = params;
    const queryParams = new URLSearchParams({
      Page: page.toString(),
      limit: limit.toString(),
    });
    const res = await api.get(
      `${API_PATH.MATCHES.VISITORS}?${queryParams.toString()}`,
    );
    return res?.data?.data ?? res?.data;
  } catch (error) {
    throw error;
  }
};

export const getMatches = async (params = {}) => {
  try {
    const { page = 1, limit = 20, status } = params;
    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });

    if (status) {
      queryParams.append("status", status);
    }

    const res = await api.get(
      `${API_PATH.MATCHES.LIST}?${queryParams.toString()}`,
    );
    return res?.data?.data;
  } catch (error) {
    throw error;
  }
};

export const getMatchRequests = async (params = {}) => {
  try {
    const { page = 1, limit = 20 } = params;
    const queryParams = new URLSearchParams({
      Page: page.toString(),
      limit: limit.toString(),
    });

    const res = await api.get(
      `${API_PATH.MATCHES.REQUESTS}?${queryParams.toString()}`,
    );
    return res?.data?.data;
  } catch (error) {
    throw error;
  }
};

export const likeUser = async (params) => {
  try {
    const res = await api.post(API_PATH.MATCHES.LIKE, params);
    return res.data;
  } catch (error) {
    throw error;
  }
};

export const notInterestedUser = async (params) => {
  try {
    const res = await api.post(API_PATH.MATCHES.NOT_INTERESTED, params);
    return res.data;
  } catch (error) {
    throw error;
  }
};

export const unlockPrivateAlbum = async (userId) => {
  try {
    const res = await api.post(
      `${API_PATH.MATCHES.UNLOCK_ALBUM}/${userId}/unlock-album`,
    );
    return res.data;
  } catch (error) {
    throw error;
  }
};

export const overrideMatch = async (matchId, params = {}) => {
  try {
    const res = await api.post(
      `${API_PATH.MATCHES.OVERRIDE}/${matchId}/override`,
      params,
    );
    return res.data;
  } catch (error) {
    throw error;
  }
};

export const respondToCircleOverride = async ({ matchId, action }) => {
  try {
    const res = await api.post(
      `${API_PATH.MATCHES.OVERRIDE}/${matchId}/override/respond`,
      { action },
    );
    return res.data;
  } catch (error) {
    throw error;
  }
};

export const unlockChat = async (matchId) => {
  try {
    const res = await api.post(
      `${API_PATH.MATCHES.LIST}/${matchId}/unlock-chat`,
    );
    return res.data;
  } catch (error) {
    throw error;
  }
};

export const unlockDirectConversation = async (conversationId) => {
  try {
    const res = await api.post(
      `${API_PATH.CHAT.UNLOCK}/${conversationId}/unlock`,
    );
    return res.data;
  } catch (error) {
    throw error;
  }
};

export const submitMatchToCircle = async (matchId) => {
  try {
    const res = await api.post(`${API_PATH.MATCHES.SUBMIT}/${matchId}/submit`);
    return res.data;
  } catch (error) {
    throw error;
  }
};

export const dismissMatch = async (matchId) => {
  try {
    const res = await api.post(
      `${API_PATH.MATCHES.DISMISS}/${matchId}/dismiss`,
    );
    return res.data;
  } catch (error) {
    throw error;
  }
};

export const castMatchVote = async ({
  matchId,
  targetUserId,
  phase,
  vote,
  tags,
  comment,
}) => {
  try {
    const res = await api.post(`${API_PATH.MATCHES.VOTES}/${matchId}/votes`, {
      targetUserId,
      phase,
      vote,
      tags,
      ...(comment ? { comment } : {}),
    });
    return res.data;
  } catch (error) {
    throw error;
  }
};

export const proceedToFinalVote = async ({ matchId, targetUserId }) => {
  try {
    const res = await api.post(
      `${API_PATH.MATCHES_EXT.PROCEED_TO_FINAL_VOTE}/${matchId}/proceed-to-final-vote`,
      { targetUserId },
    );
    return res.data;
  } catch (error) {
    throw error;
  }
};

export const getCircleInviteTemplate = async () => {
  try {
    const res = await api.get(API_PATH.CIRCLE.INVITE_TEMPLATE);
    return res?.data?.data;
  } catch (error) {
    throw error;
  }
};

export const sendCircleInviteEmail = async (params) => {
  try {
    const res = await api.post(API_PATH.CIRCLE.INVITE_EMAIL, params);
    return res?.data;
  } catch (error) {
    throw error;
  }
};

// ─── Chat ──────────────────────────────────────────────────────────────────

/** Fetch the user's conversation list */
export const getConversations = async (params = {}) => {
  try {
    const { page = 1, limit = 30 } = params;
    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    const res = await api.get(
      `${API_PATH.CHAT.CONVERSATIONS}?${queryParams.toString()}`,
    );
    return res?.data?.data ?? res?.data;
  } catch (error) {
    throw error;
  }
};

/** Get or create a conversation for a specific match */
export const getMatchConversation = async (matchId) => {
  try {
    const res = await api.get(
      `${API_PATH.CHAT.MATCH_CONVERSATION}/${matchId}/conversation`,
    );
    return res?.data?.data ?? res?.data;
  } catch (error) {
    throw error;
  }
};

/** Fetch paginated messages for a conversation */
export const getMessages = async (conversationId, params = {}) => {
  try {
    const { page, cursor, limit = 50 } = params;
    const queryParams = new URLSearchParams({ limit: limit.toString() });
    if (page) queryParams.append("page", page.toString());
    if (cursor) queryParams.append("cursor", cursor.toString());
    const res = await api.get(
      `${API_PATH.CHAT.MESSAGES}/${conversationId}/messages?${queryParams.toString()}`,
    );
    return res?.data?.data ?? res?.data;
  } catch (error) {
    throw error;
  }
};

/** Send a message via REST (fallback / media) */
export const sendMessage = async (params) => {
  try {
    const res = await api.post(API_PATH.CHAT.SEND, params);
    return res?.data?.data ?? res?.data;
  } catch (error) {
    throw error;
  }
};

/** Mark all messages in a conversation as read */
export const markMessagesRead = async (conversationId) => {
  try {
    const res = await api.post(`${API_PATH.CHAT.READ}/${conversationId}/read`);
    return res?.data;
  } catch (error) {
    throw error;
  }
};

const getAgoraStartPath = () => API_PATH.CALLS.AGORA_START;

const getAgoraJoinPath = () => API_PATH.CALLS.AGORA_JOIN;

const getCallExtendPath = () => API_PATH.CALLS.EXTEND;

const getCallEndPath = () => API_PATH.CALLS.END;

export const startAgoraCall = async (params) => {
  try {
    const res = await api.post(getAgoraStartPath(), params);
    return res?.data?.data ?? res?.data;
  } catch (error) {
    throw error;
  }
};

export const joinAgoraCall = async (params) => {
  try {
    const res = await api.post(getAgoraJoinPath(), params);
    return res?.data?.data ?? res?.data;
  } catch (error) {
    throw error;
  }
};

export const extendAgoraCall = async (params) => {
  try {
    const res = await api.post(getCallExtendPath(), params);
    return res?.data?.data ?? res?.data;
  } catch (error) {
    throw error;
  }
};

/** Report a user */
export const reportUser = async ({ reportedUserId, conversationId, reason, details }) => {
  try {
    const res = await api.post(API_PATH.CHAT.REPORT, {
      reportedUserId,
      conversationId,
      reason,
      details,
    });
    return res?.data;
  } catch (error) {
    throw error;
  }
};

/** Match-level Circle Members APIs */
export const getMatchCircleMembers = async (matchId) => {
  try {
    const url = typeof API_PATH.MATCHES.CIRCLE_MEMBERS === "function"
      ? API_PATH.MATCHES.CIRCLE_MEMBERS(matchId)
      : `/matches/${matchId}/circle-members`;
    const res = await api.get(url);
    return res?.data?.data ?? res?.data;
  } catch (error) {
    throw error;
  }
};

export const replaceMatchCircleMember = async ({ matchId, oldMemberUserId, newMemberUserId }) => {
  try {
    const url = typeof API_PATH.MATCHES.CIRCLE_MEMBERS_REPLACE === "function"
      ? API_PATH.MATCHES.CIRCLE_MEMBERS_REPLACE(matchId)
      : `/matches/${matchId}/circle-members/replace`;
    const res = await api.patch(url, { oldMemberUserId, newMemberUserId });
    return res?.data?.data ?? res?.data;
  } catch (error) {
    throw error;
  }
};

